import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Clock, Share2, Linkedin, Twitter, TrendingUp, TrendingDown, Zap, DollarSign, Shield, ChevronRight } from 'lucide-react';
import {
  ComposedChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import SEO from '../components/SEO';

/* ─────────────────────────────────────────────────────────────────────────────
   STRATEGY DEFINITIONS  –  every payoff is mathematically correct
   ───────────────────────────────────────────────────────────────────────────── */
const STRATEGIES = [
  // BASICS
  {
    id:"long_call", name:"Long Call", cat:"basics", emoji:"📈", color:"#22d3ee",
    tags:[{l:"Bullish",c:"bull"},{l:"Limited Risk",c:"safe"},{l:"Unlimited Reward",c:"reward"}],
    desc:"Pay a premium for the right to BUY shares at strike K. Your loss can never exceed the premium paid, but profits are unlimited as the stock climbs.",
    legs:[{n:1,act:"BUY", inst:"Call Option", detail:"at Strike K", cash:"− C (premium out)", pos:false}],
    guide:["Choose a strike K — usually at- or slightly out-of-the-money.","Buy 1 call contract and pay premium C upfront. This is your max loss.","At expiry: if S > K, intrinsic value = S − K. Your net P&L = (S − K) − C.","If S ≤ K the option expires worthless; you lose exactly C."],
    metrics:{profit:"Unlimited (S → ∞)",loss:"C (premium paid)",be:"K + C"},
    params:{K:100,C:5},
    defs:[{k:"K",label:"Strike Price (K)",min:70,max:130,step:1,pre:"$"},{k:"C",label:"Call Premium (C)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,S-p.K)-p.C,
  },
  {
    id:"long_put", name:"Long Put", cat:"basics", emoji:"📉", color:"#f87171",
    tags:[{l:"Bearish",c:"bear"},{l:"Limited Risk",c:"safe"},{l:"High Downside Reward",c:"reward"}],
    desc:"Pay a premium for the right to SELL shares at strike K. Profits as the stock falls below K. Max loss is the premium.",
    legs:[{n:1,act:"BUY", inst:"Put Option", detail:"at Strike K", cash:"− P (premium out)", pos:false}],
    guide:["Choose a strike K — usually at- or slightly out-of-the-money.","Buy 1 put contract and pay premium P upfront.","At expiry: if S < K, intrinsic value = K − S. Net P&L = (K − S) − P.","If S ≥ K the put expires worthless; you lose exactly P."],
    metrics:{profit:"K − P (if stock → $0)",loss:"P (premium paid)",be:"K − P"},
    params:{K:100,P:5},
    defs:[{k:"K",label:"Strike Price (K)",min:70,max:130,step:1,pre:"$"},{k:"P",label:"Put Premium (P)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,p.K-S)-p.P,
  },
  {
    id:"short_call", name:"Short Call", cat:"basics", emoji:"⬇️", color:"#fb923c",
    tags:[{l:"Bearish/Neutral",c:"bear"},{l:"Unlimited Risk",c:"danger"},{l:"Limited Reward",c:"cap"}],
    desc:"Sell a call and collect the premium now. Profit if the stock stays below K, but face unlimited loss if it surges above K.",
    legs:[{n:1,act:"SELL",inst:"Call Option", detail:"at Strike K", cash:"+ C (premium in)", pos:true}],
    guide:["Write (sell) 1 call at strike K — receive premium C immediately.","C is your absolute maximum profit.","If S rises above K you must deliver shares at K. Loss grows without limit above K + C.","Requires margin. Only suitable when you are confident the stock won't spike."],
    metrics:{profit:"C (premium received)",loss:"Unlimited (S → ∞)",be:"K + C"},
    params:{K:100,C:5},
    defs:[{k:"K",label:"Strike Price (K)",min:70,max:130,step:1,pre:"$"},{k:"C",label:"Call Premium (C)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K"],
    payoff:(S,p)=> p.C-Math.max(0,S-p.K),
  },
  {
    id:"short_put", name:"Short Put", cat:"basics", emoji:"⬆️", color:"#a78bfa",
    tags:[{l:"Bullish/Neutral",c:"bull"},{l:"High Risk",c:"danger"},{l:"Limited Reward",c:"cap"}],
    desc:"Sell a put and collect the premium. Profit if the stock stays above K, but you're obligated to buy shares at K if it falls hard.",
    legs:[{n:1,act:"SELL",inst:"Put Option", detail:"at Strike K", cash:"+ P (premium in)", pos:true}],
    guide:["Write (sell) 1 put at strike K — receive premium P immediately.","P is your absolute maximum profit.","If S falls below K you must buy shares at K. Loss floor = K − P (stock → $0).","Requires margin. Popular as a 'cash-secured put' when you want to acquire shares cheaply."],
    metrics:{profit:"P (premium received)",loss:"K − P (stock → $0)",be:"K − P"},
    params:{K:100,P:5},
    defs:[{k:"K",label:"Strike Price (K)",min:70,max:130,step:1,pre:"$"},{k:"P",label:"Put Premium (P)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K"],
    payoff:(S,p)=> p.P-Math.max(0,p.K-S),
  },
  // SPREADS
  {
    id:"bull_call_spread", name:"Bull Call Spread", cat:"spreads", emoji:"🐂", color:"#34d399",
    tags:[{l:"Bullish",c:"bull"},{l:"Limited Risk",c:"safe"},{l:"Limited Reward",c:"cap"},{l:"Net Debit",c:"debit"}],
    desc:"Buy a lower-strike call (K₁) and sell a higher-strike call (K₂). Lower cost than a naked call, but upside is capped at K₂.",
    legs:[{n:1,act:"BUY",inst:"Call Option",detail:"Lower Strike K₁",cash:"− C₁",pos:false},{n:2,act:"SELL",inst:"Call Option",detail:"Upper Strike K₂",cash:"+ C₂",pos:true}],
    guide:["K₂ must be greater than K₁. Both options share the same expiry.","Buy 1 call at K₁ (ATM or slightly ITM) — pay C₁.","Sell 1 call at K₂ (OTM) — receive C₂. Net debit = C₁ − C₂.","Max profit = (K₂ − K₁) − (C₁ − C₂) when S ≥ K₂ at expiry.","Max loss = net debit (C₁ − C₂) when S ≤ K₁ at expiry."],
    metrics:{profit:"(K₂−K₁) − (C₁−C₂)",loss:"Net debit C₁ − C₂",be:"K₁ + (C₁ − C₂)"},
    params:{K1:95,K2:110,C1:8,C2:3},
    defs:[{k:"K1",label:"Lower Strike K₁",min:60,max:120,step:1,pre:"$"},{k:"K2",label:"Upper Strike K₂",min:70,max:145,step:1,pre:"$"},{k:"C1",label:"Call Premium C₁",min:0.5,max:25,step:0.5,pre:"$"},{k:"C2",label:"Call Premium C₂",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,S-p.K1)-Math.max(0,S-p.K2)-(p.C1-p.C2),
  },
  {
    id:"bear_put_spread", name:"Bear Put Spread", cat:"spreads", emoji:"🐻", color:"#f472b6",
    tags:[{l:"Bearish",c:"bear"},{l:"Limited Risk",c:"safe"},{l:"Limited Reward",c:"cap"},{l:"Net Debit",c:"debit"}],
    desc:"Buy a higher-strike put (K₂) and sell a lower-strike put (K₁). Bearish strategy at reduced cost, with capped maximum profit.",
    legs:[{n:1,act:"BUY",inst:"Put Option",detail:"Higher Strike K₂",cash:"− P₂",pos:false},{n:2,act:"SELL",inst:"Put Option",detail:"Lower Strike K₁",cash:"+ P₁",pos:true}],
    guide:["K₂ must be greater than K₁. Both options share the same expiry.","Buy 1 put at K₂ (ATM or slightly ITM) — pay P₂.","Sell 1 put at K₁ (OTM, lower) — receive P₁. Net debit = P₂ − P₁.","Max profit = (K₂ − K₁) − (P₂ − P₁) when S ≤ K₁ at expiry.","Max loss = net debit (P₂ − P₁) when S ≥ K₂ at expiry."],
    metrics:{profit:"(K₂−K₁) − (P₂−P₁)",loss:"Net debit P₂ − P₁",be:"K₂ − (P₂ − P₁)"},
    params:{K1:90,K2:105,P1:2,P2:7},
    defs:[{k:"K1",label:"Lower Strike K₁",min:60,max:120,step:1,pre:"$"},{k:"K2",label:"Upper Strike K₂",min:70,max:140,step:1,pre:"$"},{k:"P2",label:"Put Premium P₂",min:0.5,max:25,step:0.5,pre:"$"},{k:"P1",label:"Put Premium P₁",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,p.K2-S)-Math.max(0,p.K1-S)-(p.P2-p.P1),
  },
  // VOLATILITY
  {
    id:"straddle", name:"Long Straddle", cat:"volatility", emoji:"⚡", color:"#fbbf24",
    tags:[{l:"Neutral",c:"neutral"},{l:"Big Move Required",c:"vol"},{l:"Limited Risk",c:"safe"}],
    desc:"Buy an ATM call AND an ATM put at the SAME strike K. You profit from a large move in either direction — direction-agnostic volatility bet.",
    legs:[{n:1,act:"BUY",inst:"Call Option",detail:"Strike K (ATM)",cash:"− C (premium out)",pos:false},{n:2,act:"BUY",inst:"Put Option",detail:"Strike K (ATM)",cash:"− P (premium out)",pos:false}],
    guide:["Both legs use the SAME strike K (usually ATM) and same expiry.","Buy 1 call — pay C. Buy 1 put — pay P. Total cost = C + P.","At expiry: P&L = max(0, S−K) + max(0, K−S) − (C+P).","Upper breakeven = K + (C+P). Lower breakeven = K − (C+P).","Maximum loss occurs if stock pins exactly at K — both options expire worthless."],
    metrics:{profit:"Unlimited (up) / K−(C+P) (down)",loss:"C + P (stock pins at K)",be:"K±(C+P)"},
    params:{K:100,C:5,P:5},
    defs:[{k:"K",label:"Strike K (same for both)",min:70,max:130,step:1,pre:"$"},{k:"C",label:"Call Premium (C)",min:0.5,max:20,step:0.5,pre:"$"},{k:"P",label:"Put Premium (P)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["K"],
    payoff:(S,p)=> Math.max(0,S-p.K)+Math.max(0,p.K-S)-(p.C+p.P),
  },
  {
    id:"strangle", name:"Long Strangle", cat:"volatility", emoji:"🌀", color:"#60a5fa",
    tags:[{l:"Neutral",c:"neutral"},{l:"Big Move Required",c:"vol"},{l:"Cheaper than Straddle",c:"safe"}],
    desc:"Buy an OTM put at K₁ and an OTM call at K₂. Cheaper than a straddle, but requires an even larger move to profit.",
    legs:[{n:1,act:"BUY",inst:"Put Option",detail:"OTM Put Strike K₁ (lower)",cash:"− P (premium out)",pos:false},{n:2,act:"BUY",inst:"Call Option",detail:"OTM Call Strike K₂ (higher)",cash:"− C (premium out)",pos:false}],
    guide:["K₁ < current stock price < K₂ (both out-of-the-money).","Buy 1 OTM put at K₁ — pay P. Buy 1 OTM call at K₂ — pay C.","Total cost = P + C (cheaper than a straddle).","Lower breakeven = K₁ − (P+C). Upper breakeven = K₂ + (P+C).","Loss if stock stays between K₁ and K₂ at expiry."],
    metrics:{profit:"Unlimited (up) / K₁−(C+P) (down)",loss:"C + P (stock between K₁ & K₂)",be:"K₁−(C+P) and K₂+(C+P)"},
    params:{K1:92,K2:108,P:3,C:3},
    defs:[{k:"K1",label:"OTM Put Strike K₁",min:60,max:115,step:1,pre:"$"},{k:"K2",label:"OTM Call Strike K₂",min:75,max:140,step:1,pre:"$"},{k:"P",label:"Put Premium (P)",min:0.5,max:15,step:0.5,pre:"$"},{k:"C",label:"Call Premium (C)",min:0.5,max:15,step:0.5,pre:"$"}],
    refs:["K1","K2"],
    payoff:(S,p)=> Math.max(0,p.K1-S)+Math.max(0,S-p.K2)-(p.C+p.P),
  },
  // INCOME
  {
    id:"butterfly", name:"Butterfly Spread", cat:"income", emoji:"🦋", color:"#2dd4bf",
    tags:[{l:"Neutral",c:"neutral"},{l:"Net Debit",c:"debit"},{l:"3 Strikes",c:"complex"},{l:"Precise",c:"cap"}],
    desc:"Buy 1 lower-strike call, sell 2 middle-strike calls, buy 1 higher-strike call. Max profit if stock pins at K₂ at expiry.",
    legs:[{n:1,act:"BUY",inst:"Call Option",detail:"Lower Strike K₁",cash:"− C₁",pos:false},{n:2,act:"SELL",inst:"Call Option",detail:"Middle Strike K₂ (×2)",cash:"+ 2×C₂",pos:true},{n:3,act:"BUY",inst:"Call Option",detail:"Upper Strike K₃",cash:"− C₃",pos:false}],
    guide:["Wings must be equidistant: K₂ − K₁ = K₃ − K₂.","Buy 1 call at K₁ — pay C₁. Sell 2 calls at K₂ — receive 2×C₂. Buy 1 call at K₃ — pay C₃.","Net debit = C₁ + C₃ − 2×C₂ (should be small & positive).","Max profit = (K₂ − K₁) − net debit when stock closes exactly at K₂.","Breakeven: K₁ + debit (lower) and K₃ − debit (upper)."],
    metrics:{profit:"(K₂−K₁) − net debit (stock pins at K₂)",loss:"Net debit = C₁+C₃−2×C₂",be:"K₁+debit & K₃−debit"},
    params:{K1:90,K2:100,K3:110,C1:12,C2:7,C3:3},
    defs:[{k:"K1",label:"Lower Strike K₁",min:60,max:115,step:1,pre:"$"},{k:"K2",label:"Middle Strike K₂",min:70,max:130,step:1,pre:"$"},{k:"K3",label:"Upper Strike K₃",min:80,max:145,step:1,pre:"$"},{k:"C1",label:"Call Premium C₁",min:1,max:30,step:0.5,pre:"$"}],
    refs:["K1","K2","K3"],
    payoff:(S,p)=>{const gross=Math.max(0,S-p.K1)-2*Math.max(0,S-p.K2)+Math.max(0,S-p.K3);const debit=p.C1-2*p.C2+p.C3;return gross-debit;},
  },
  {
    id:"covered_call", name:"Covered Call", cat:"income", emoji:"🛡️", color:"#f59e0b",
    tags:[{l:"Mildly Bullish",c:"bull"},{l:"Stock Required",c:"stock"},{l:"Income",c:"credit"},{l:"Capped Upside",c:"cap"}],
    desc:"Own 100 shares (bought at S₀), then sell 1 call at strike K above your cost. Earns premium income but caps upside at K.",
    legs:[{n:1,act:"BUY",inst:"Stock (100 shares)",detail:"Purchased at price S₀",cash:"− S₀ × 100",pos:false},{n:2,act:"SELL",inst:"Call Option",detail:"Strike K (above S₀, OTM)",cash:"+ C (premium in)",pos:true}],
    guide:["You MUST already own (or simultaneously buy) 100 shares at S₀.","Sell 1 call at strike K (choose K above S₀ — typically 5–10% OTM).","Effective cost basis is reduced to S₀ − C.","If S stays below K at expiry: keep shares + pocket full premium C.","If S rises above K: shares are called away at K. Max profit = (K − S₀) + C."],
    metrics:{profit:"(K − S₀) + C (shares called at K)",loss:"S₀ − C (stock → $0)",be:"S₀ − C"},
    params:{S0:100,K:110,C:4},
    defs:[{k:"S0",label:"Stock Purchase Price S₀",min:60,max:140,step:1,pre:"$"},{k:"K",label:"Call Strike K (above S₀)",min:65,max:160,step:1,pre:"$"},{k:"C",label:"Call Premium (C)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["S0","K"],
    payoff:(S,p)=> (S-p.S0)-Math.max(0,S-p.K)+p.C,
  },
  // HEDGING
  {
    id:"protective_put", name:"Protective Put", cat:"hedging", emoji:"🔐", color:"#e879f9",
    tags:[{l:"Bullish",c:"bull"},{l:"Stock Required",c:"stock"},{l:"Insurance",c:"safe"},{l:"Downside Floor",c:"safe"}],
    desc:"Own 100 shares (at S₀) and buy a put at strike K as portfolio insurance. Sets a hard floor on losses while keeping unlimited upside.",
    legs:[{n:1,act:"BUY",inst:"Stock (100 shares)",detail:"Purchased at price S₀",cash:"− S₀ × 100",pos:false},{n:2,act:"BUY",inst:"Put Option",detail:"Strike K (your floor)",cash:"− P (insurance premium)",pos:false}],
    guide:["You MUST already own (or simultaneously buy) 100 shares at S₀.","Buy 1 put at strike K — pay premium P. K is your guaranteed exit price on the downside.","Total outlay = S₀ + P per share. Breakeven = S₀ + P.","If stock falls below K: put gains in value, offsetting stock loss. Loss is floored at (S₀ − K) + P.","If stock rises: put expires worthless — you keep all gains, only cost was P."],
    metrics:{profit:"Unlimited (full upside retained minus P)",loss:"(S₀ − K) + P (hard floor at K)",be:"S₀ + P"},
    params:{S0:100,K:93,P:3},
    defs:[{k:"S0",label:"Stock Purchase Price S₀",min:60,max:140,step:1,pre:"$"},{k:"K",label:"Put Strike / Floor (K)",min:50,max:138,step:1,pre:"$"},{k:"P",label:"Insurance Premium (P)",min:0.5,max:20,step:0.5,pre:"$"}],
    refs:["S0","K"],
    payoff:(S,p)=> (S-p.S0) + Math.max(0,p.K-S) - p.P,
  },
];

const TC = {
  bull:"#bbf7d0",bear:"#fca5a5",neutral:"#93c5fd",safe:"#6ee7b7",
  danger:"#fdba74",reward:"#d9f99d",cap:"#cbd5e1",debit:"#fde68a",
  credit:"#a5f3fc",range:"#c4b5fd",complex:"#f9a8d4",stock:"#fed7aa",vol:"#e9d5ff",
};

function generateData(strat, params) {
  const refs = strat.refs.map(r=>params[r]).filter(Boolean);
  if(!refs.length) return [];
  const lo = Math.min(...refs)-28, hi = Math.max(...refs)+28;
  const out = [];
  for(let S=lo;S<=hi;S+=0.5){
    const v = +strat.payoff(S,params).toFixed(2);
    out.push({S:+S.toFixed(1), payoff:v, pos:Math.max(0,v), neg:Math.min(0,v)});
  }
  return out;
}

const REF_LABEL = {K:"K",K1:"K₁",K2:"K₂",K3:"K₃",K4:"K₄",S0:"S₀"};

function ChartTip({active,payload,label}){
  if(!active||!payload?.length) return null;
  const v = payload.find(p=>p.dataKey==="payoff")?.value??0;
  return (
    <div style={{background:"#0a0e1a",border:"1px solid #1e293b",borderRadius:"12px",padding:"12px",minWidth:"120px"}}>
      <div style={{fontSize:"10px",color:"#64748b",marginBottom:"2px",fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.1em"}}>Price at Expiry</div>
      <div style={{color:"white",fontWeight:900,fontSize:"18px",fontFamily:"monospace"}}>${label}</div>
      <div style={{fontSize:"10px",color:"#64748b",marginTop:"8px",marginBottom:"2px",fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.1em"}}>Net P&L</div>
      <div style={{fontWeight:900,fontSize:"20px",fontFamily:"monospace",color:v>=0?"#4ade80":"#f87171"}}>{v>=0?"+":""}{v.toFixed(2)}</div>
    </div>
  );
}

function MiniPayoffChart({strat,params}){
  const data = useMemo(()=>generateData(strat,params),[strat,params]);
  if(!data.length) return null;
  const vals = data.map(d=>d.payoff);
  const maxV=Math.max(...vals),minV=Math.min(...vals);
  const pad=(maxV-minV)*0.2+3;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={data} margin={{top:10,right:10,left:0,bottom:0}}>
        <defs>
          <linearGradient id={`gp-${strat.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4ade80" stopOpacity={0.4}/>
            <stop offset="100%" stopColor="#4ade80" stopOpacity={0.02}/>
          </linearGradient>
          <linearGradient id={`gn-${strat.id}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#f87171" stopOpacity={0.4}/>
            <stop offset="100%" stopColor="#f87171" stopOpacity={0.02}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="#1e293b" vertical={false}/>
        <XAxis dataKey="S" stroke="#1e293b" tick={{fill:"#475569",fontSize:9,fontFamily:"monospace"}} tickFormatter={v=>`$${v}`} tickLine={false}/>
        <YAxis stroke="#1e293b" tick={{fill:"#475569",fontSize:9,fontFamily:"monospace"}} tickFormatter={v=>`${v>0?"+":""}${v}`} domain={[minV-pad,maxV+pad]} tickLine={false} width={40}/>
        <Tooltip content={<ChartTip/>}/>
        <ReferenceLine y={0} stroke="#334155" strokeWidth={1.5} strokeDasharray="5 3"/>
        {strat.refs.map(r=>(
          <ReferenceLine key={r} x={params[r]} stroke="#334155" strokeWidth={1} strokeDasharray="3 4"
            label={{value:REF_LABEL[r]||r,fill:"#64748b",fontSize:9,fontFamily:"monospace",position:"insideTopRight"}}/>
        ))}
        <Area type="monotone" dataKey="pos" stroke="none" fill={`url(#gp-${strat.id})`} dot={false} isAnimationActive={false}/>
        <Area type="monotone" dataKey="neg" stroke="none" fill={`url(#gn-${strat.id})`} dot={false} isAnimationActive={false}/>
        <Line type="monotone" dataKey="payoff" stroke={strat.color} strokeWidth={2.5} dot={false}
          activeDot={{r:4,fill:strat.color,stroke:"#0a0e1a",strokeWidth:2}} isAnimationActive={false}/>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// Interactive Strategy Card Component
function StrategyCard({ strategy }) {
  const [params, setParams] = useState({...strategy.params});
  
  const liveMetrics = useMemo(()=>{
    const data = generateData(strategy,params);
    if(!data.length) return null;
    const vs = data.map(d=>d.payoff);
    const mx = Math.max(...vs), mn = Math.min(...vs);
    return {mx,mn};
  },[strategy,params]);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0a0e1a 0%, #111827 100%)',
      borderRadius: '20px',
      border: `1px solid ${strategy.color}30`,
      padding: '32px',
      marginBottom: '40px',
      boxShadow: `0 4px 40px ${strategy.color}15`
    }}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',gap:'20px',marginBottom:'24px'}}>
        <div style={{
          width:'56px',height:'56px',borderRadius:'16px',
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:'28px',background:`${strategy.color}15`,border:`1px solid ${strategy.color}30`
        }}>
          {strategy.emoji}
        </div>
        <div style={{flex:1}}>
          <h3 style={{fontSize:'1.5rem',fontWeight:800,color:'white',marginBottom:'8px'}}>{strategy.name}</h3>
          <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
            {strategy.tags.map(t=>(
              <span key={t.l} style={{
                padding:'4px 12px',borderRadius:'20px',fontSize:'11px',fontWeight:700,
                color:TC[t.c],background:`${TC[t.c]}15`,border:`1px solid ${TC[t.c]}30`
              }}>{t.l}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Description */}
      <p style={{color:'#94a3b8',fontSize:'1rem',lineHeight:1.8,marginBottom:'28px'}}>{strategy.desc}</p>

      {/* Two Column Layout */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'24px'}}>
        {/* Left: Chart */}
        <div style={{
          background:'#0d1117',borderRadius:'16px',padding:'20px',
          border:'1px solid #1e293b'
        }}>
          <div style={{fontSize:'11px',fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'16px'}}>
            Payoff at Expiration
          </div>
          <MiniPayoffChart strat={strategy} params={params}/>
          
          {/* Legend */}
          <div style={{display:'flex',gap:'16px',marginTop:'12px',flexWrap:'wrap'}}>
            {[{color:"#4ade80",label:"Profit"},{color:"#f87171",label:"Loss"},{color:"#334155",label:"Strike"}].map(l=>(
              <span key={l.label} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'10px',color:'#64748b'}}>
                <span style={{width:'16px',height:'2px',borderRadius:'1px',background:l.color,display:'inline-block'}}/>
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Parameters & Metrics */}
        <div>
          {/* Parameters */}
          <div style={{
            background:'#0d1117',borderRadius:'16px',padding:'20px',
            border:'1px solid #1e293b',marginBottom:'16px'
          }}>
            <div style={{fontSize:'11px',fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'16px'}}>
              Adjust Parameters
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
              {strategy.defs.slice(0,4).map(d=>(
                <div key={d.k}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'6px'}}>
                    <label style={{fontSize:'10px',color:'#64748b'}}>{d.label}</label>
                    <span style={{fontSize:'11px',fontWeight:800,fontFamily:'monospace',color:strategy.color}}>{d.pre}{params[d.k]}</span>
                  </div>
                  <input type="range" min={d.min} max={d.max} step={d.step} value={params[d.k]}
                    onChange={e=>setParams(prev=>({...prev,[d.k]:+e.target.value}))}
                    style={{width:'100%',height:'3px',accentColor:strategy.color}}/>
                </div>
              ))}
            </div>
          </div>

          {/* Live Metrics */}
          {liveMetrics && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div style={{
                background:'rgba(74,222,128,0.08)',borderRadius:'12px',padding:'16px',
                border:'1px solid rgba(74,222,128,0.2)',textAlign:'center'
              }}>
                <div style={{fontSize:'9px',fontWeight:800,color:'#4ade80',opacity:0.7,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'4px'}}>Max Profit</div>
                <div style={{fontSize:'18px',fontWeight:900,fontFamily:'monospace',color:'#4ade80'}}>
                  {liveMetrics.mx>9000?"∞":`+${liveMetrics.mx.toFixed(1)}`}
                </div>
              </div>
              <div style={{
                background:'rgba(248,113,113,0.08)',borderRadius:'12px',padding:'16px',
                border:'1px solid rgba(248,113,113,0.2)',textAlign:'center'
              }}>
                <div style={{fontSize:'9px',fontWeight:800,color:'#f87171',opacity:0.7,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'4px'}}>Max Loss</div>
                <div style={{fontSize:'18px',fontWeight:900,fontFamily:'monospace',color:'#f87171'}}>
                  {liveMetrics.mn<-9000?"-∞":`${liveMetrics.mn.toFixed(1)}`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Strategy Legs */}
      <div style={{marginTop:'24px',paddingTop:'24px',borderTop:'1px solid #1e293b'}}>
        <div style={{fontSize:'11px',fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'16px'}}>
          How to Build This Strategy
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:'12px'}}>
          {strategy.legs.map(leg=>(
            <div key={leg.n} style={{
              background:'#0d1117',borderRadius:'12px',padding:'16px',flex:'1',minWidth:'200px',
              border:'1px solid #1e293b',display:'flex',gap:'12px',alignItems:'flex-start'
            }}>
              <div style={{
                width:'24px',height:'24px',borderRadius:'6px',background:'#1e293b',
                color:'#64748b',fontSize:'11px',fontWeight:800,
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0
              }}>{leg.n}</div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px'}}>
                  <span style={{
                    fontSize:'10px',fontWeight:800,padding:'2px 8px',borderRadius:'4px',
                    background:leg.act==="BUY"?"rgba(74,222,128,0.15)":"rgba(248,113,113,0.15)",
                    color:leg.act==="BUY"?"#4ade80":"#f87171",
                    border:`1px solid ${leg.act==="BUY"?"rgba(74,222,128,0.3)":"rgba(248,113,113,0.3)"}`
                  }}>{leg.act}</span>
                  <span style={{fontSize:'12px',fontWeight:600,color:'#e2e8f0'}}>{leg.inst}</span>
                </div>
                <div style={{fontSize:'11px',color:'#64748b'}}>{leg.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const DerivativesStrategiesBlog = () => {
  const post = {
    title: "The Complete Guide to Options Strategies: From Basics to Advanced",
    excerpt: "Master 12 essential derivatives strategies with interactive payoff diagrams. Learn when to use each strategy, understand risk/reward profiles, and practice with our live calculator.",
    date: "December 4, 2025",
    tags: ["Options", "Derivatives", "Trading Strategies", "Risk Management", "CIRO Exam"],
    keywords: "options strategies, derivatives trading, call options, put options, spreads, straddles, iron condor, covered call, protective put, options payoff diagram"
  };

  const wordCount = 3500;
  const readTime = Math.ceil(wordCount / 200);

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title={`${post.title} | Vitta Finance Academy Blog`}
        description={post.excerpt}
        keywords={post.keywords}
        ogTitle={post.title}
        ogDescription={post.excerpt}
      />

      {/* Hero Section */}
      <section style={{
        padding: '100px 0 60px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #7c3aed 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,overflow:'hidden',pointerEvents:'none'}}>
          <div style={{position:'absolute',top:'10%',left:'5%',width:'300px',height:'300px',background:'radial-gradient(circle,rgba(34,211,238,0.1) 0%,transparent 70%)',borderRadius:'50%'}}/>
          <div style={{position:'absolute',bottom:'20%',right:'10%',width:'400px',height:'400px',background:'radial-gradient(circle,rgba(248,113,113,0.1) 0%,transparent 70%)',borderRadius:'50%'}}/>
        </div>
        
        <div className="container" style={{position:'relative',zIndex:1}}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* Breadcrumb */}
            <div style={{ marginBottom: '24px' }}>
              <Link to="/blog" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'rgba(255,255,255,0.8)',
                textDecoration: 'none',
                fontSize: '0.95rem'
              }}>
                <ArrowLeft size={18} /> Back to Blog
              </Link>
            </div>

            {/* Tags */}
            <div style={{display:'flex',flexWrap:'wrap',gap:'10px',marginBottom:'20px'}}>
              {post.tags.slice(0, 4).map((tag, idx) => (
                <span key={idx} style={{
                  padding:'6px 14px',
                  backgroundColor:'rgba(212, 175, 55, 0.2)',
                  border:'1px solid var(--gold-primary)',
                  borderRadius:'20px',
                  fontSize:'0.85rem',
                  color:'var(--gold-primary)',
                  fontWeight:500
                }}>
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              {post.title}
            </h1>

            {/* Meta Info */}
            <div style={{
              display:'flex',alignItems:'center',gap:'24px',flexWrap:'wrap',
              color:'rgba(255,255,255,0.9)'
            }}>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <Calendar size={18} />
                <span>{post.date}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <Clock size={18} />
                <span>{readTime} min read</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'8px',
                padding:'6px 12px',background:'rgba(74,222,128,0.2)',borderRadius:'20px',
                border:'1px solid rgba(74,222,128,0.3)'
              }}>
                <Zap size={16} color="#4ade80"/>
                <span style={{color:'#4ade80',fontWeight:600,fontSize:'0.9rem'}}>Interactive Charts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{padding:'80px 0',backgroundColor:'#0f172a'}}>
        <div className="container">
          <div style={{maxWidth:'1100px',margin:'0 auto'}}>
            
            {/* Introduction */}
            <div style={{
              backgroundColor:'#1e293b',
              padding:'40px',
              borderRadius:'20px',
              marginBottom:'60px',
              border:'1px solid #334155'
            }}>
              <p style={{
                fontSize:'1.25rem',
                lineHeight:1.9,
                color:'#94a3b8',
                marginBottom:'24px'
              }}>
                Options strategies are the building blocks of sophisticated portfolio management. Whether you're preparing for the <strong style={{color:'white'}}>CIRO Derivatives Exam</strong> or looking to enhance your trading toolkit, understanding these strategies is essential.
              </p>
              <p style={{fontSize:'1.1rem',lineHeight:1.8,color:'#64748b'}}>
                This interactive guide covers <strong style={{color:'#22d3ee'}}>all 11 essential strategies</strong> with live payoff calculators. Adjust the parameters yourself to see how changes in strike prices and premiums affect your profit/loss profile.
              </p>
              
              {/* Quick Navigation */}
              <div style={{marginTop:'32px',paddingTop:'32px',borderTop:'1px solid #334155'}}>
                <div style={{fontSize:'12px',fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'16px'}}>
                  Jump to Strategy
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'10px'}}>
                  {STRATEGIES.map(s=>(
                    <a key={s.id} href={`#${s.id}`} style={{
                      display:'flex',alignItems:'center',gap:'8px',
                      padding:'8px 16px',background:`${s.color}15`,borderRadius:'10px',
                      border:`1px solid ${s.color}30`,textDecoration:'none',
                      color:s.color,fontSize:'13px',fontWeight:600,
                      transition:'all 0.2s ease'
                    }}>
                      <span>{s.emoji}</span>
                      <span>{s.name}</span>
                      <ChevronRight size={14}/>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Category: Basics */}
            <div style={{marginBottom:'60px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'32px'}}>
                <div style={{
                  width:'48px',height:'48px',borderRadius:'12px',
                  background:'linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                  <TrendingUp size={24} color="white"/>
                </div>
                <div>
                  <h2 style={{fontSize:'1.75rem',fontWeight:800,color:'white',margin:0}}>Basic Strategies</h2>
                  <p style={{color:'#64748b',margin:0,fontSize:'0.95rem'}}>The foundation of all options trading</p>
                </div>
              </div>
              
              <div id="long_call"><StrategyCard strategy={STRATEGIES[0]}/></div>
              <div id="long_put"><StrategyCard strategy={STRATEGIES[1]}/></div>
              <div id="short_call"><StrategyCard strategy={STRATEGIES[2]}/></div>
              <div id="short_put"><StrategyCard strategy={STRATEGIES[3]}/></div>
            </div>

            {/* Category: Spreads */}
            <div style={{marginBottom:'60px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'32px'}}>
                <div style={{
                  width:'48px',height:'48px',borderRadius:'12px',
                  background:'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                  <TrendingUp size={24} color="white"/>
                </div>
                <div>
                  <h2 style={{fontSize:'1.75rem',fontWeight:800,color:'white',margin:0}}>Spread Strategies</h2>
                  <p style={{color:'#64748b',margin:0,fontSize:'0.95rem'}}>Limit risk while maintaining directional exposure</p>
                </div>
              </div>
              
              <div id="bull_call_spread"><StrategyCard strategy={STRATEGIES[4]}/></div>
              <div id="bear_put_spread"><StrategyCard strategy={STRATEGIES[5]}/></div>
            </div>

            {/* Category: Volatility */}
            <div style={{marginBottom:'60px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'32px'}}>
                <div style={{
                  width:'48px',height:'48px',borderRadius:'12px',
                  background:'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                  <Zap size={24} color="white"/>
                </div>
                <div>
                  <h2 style={{fontSize:'1.75rem',fontWeight:800,color:'white',margin:0}}>Volatility Strategies</h2>
                  <p style={{color:'#64748b',margin:0,fontSize:'0.95rem'}}>Profit from big moves in either direction</p>
                </div>
              </div>
              
              <div id="straddle"><StrategyCard strategy={STRATEGIES[6]}/></div>
              <div id="strangle"><StrategyCard strategy={STRATEGIES[7]}/></div>
            </div>

            {/* Category: Income */}
            <div style={{marginBottom:'60px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'32px'}}>
                <div style={{
                  width:'48px',height:'48px',borderRadius:'12px',
                  background:'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                  <DollarSign size={24} color="white"/>
                </div>
                <div>
                  <h2 style={{fontSize:'1.75rem',fontWeight:800,color:'white',margin:0}}>Income Strategies</h2>
                  <p style={{color:'#64748b',margin:0,fontSize:'0.95rem'}}>Generate premium income in range-bound markets</p>
                </div>
              </div>
              
              <div id="butterfly"><StrategyCard strategy={STRATEGIES[8]}/></div>
              <div id="covered_call"><StrategyCard strategy={STRATEGIES[9]}/></div>
            </div>

            {/* Category: Hedging */}
            <div style={{marginBottom:'60px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'32px'}}>
                <div style={{
                  width:'48px',height:'48px',borderRadius:'12px',
                  background:'linear-gradient(135deg, #e879f9 0%, #d946ef 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                  <Shield size={24} color="white"/>
                </div>
                <div>
                  <h2 style={{fontSize:'1.75rem',fontWeight:800,color:'white',margin:0}}>Hedging Strategies</h2>
                  <p style={{color:'#64748b',margin:0,fontSize:'0.95rem'}}>Protect your portfolio from downside risk</p>
                </div>
              </div>
              
              <div id="protective_put"><StrategyCard strategy={STRATEGIES[10]}/></div>
            </div>

            {/* Full Strategy Lab CTA */}
            <div style={{
              background:'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
              borderRadius:'24px',
              padding:'48px',
              textAlign:'center',
              marginTop:'60px'
            }}>
              <h2 style={{fontSize:'2rem',fontWeight:800,color:'white',marginBottom:'16px'}}>
                Try the Interactive Strategy Lab
              </h2>
              <p style={{fontSize:'1.1rem',color:'rgba(255,255,255,0.8)',marginBottom:'32px',maxWidth:'600px',margin:'0 auto 32px'}}>
                Want a more focused experience? Visit our dedicated Derivatives Strategy Lab for full-screen interactive charts, detailed construction guides, and side-by-side comparisons.
              </p>
              <Link to="/derivatives-strategy-lab" style={{
                display:'inline-flex',alignItems:'center',gap:'12px',
                padding:'16px 32px',background:'var(--gold-primary)',
                borderRadius:'12px',color:'#1e293b',fontWeight:700,
                fontSize:'1.1rem',textDecoration:'none',
                boxShadow:'0 4px 20px rgba(212,175,55,0.3)'
              }}>
                Open Strategy Lab <ChevronRight size={20}/>
              </Link>
            </div>

            {/* Share Section */}
            <div style={{
              display:'flex',justifyContent:'space-between',alignItems:'center',
              marginTop:'48px',paddingTop:'32px',borderTop:'1px solid #334155',
              flexWrap:'wrap',gap:'20px'
            }}>
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <Tag size={18} color="#d4af37"/>
                <span style={{fontWeight:600,color:'white'}}>Tags:</span>
                {post.tags.map((tag, idx) => (
                  <span key={idx} style={{
                    padding:'6px 14px',
                    backgroundColor:'#1e293b',
                    borderRadius:'6px',
                    fontSize:'0.9rem',
                    color:'#94a3b8'
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
              
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <span style={{color:'#64748b',fontSize:'0.9rem'}}>Share:</span>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display:'flex',alignItems:'center',justifyContent:'center',
                    width:'40px',height:'40px',backgroundColor:'#0077b5',
                    borderRadius:'8px',color:'white'
                  }}
                >
                  <Linkedin size={18}/>
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display:'flex',alignItems:'center',justifyContent:'center',
                    width:'40px',height:'40px',backgroundColor:'#1da1f2',
                    borderRadius:'8px',color:'white'
                  }}
                >
                  <Twitter size={18}/>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{
        padding:'80px 0',
        background:'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        textAlign:'center'
      }}>
        <div className="container">
          <h2 style={{fontSize:'2rem',fontWeight:800,color:'white',marginBottom:'16px'}}>
            Ready to Master Derivatives?
          </h2>
          <p style={{
            fontSize:'1.1rem',color:'#94a3b8',marginBottom:'32px',
            maxWidth:'600px',margin:'0 auto 32px'
          }}>
            Join thousands of successful candidates who passed their CIRO Derivatives Exam with Vitta Finance Academy.
          </p>
          <div style={{display:'flex',gap:'16px',justifyContent:'center',flexWrap:'wrap'}}>
            <a href="https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout" target="_blank" rel="noopener noreferrer" className="btn-gold" style={{fontSize:'1rem',padding:'16px 32px'}}>
              Start Derivatives Course — $29.99/mo
            </a>
            <Link to="/diagnostic-tool" className="btn-secondary" style={{fontSize:'1rem',padding:'16px 32px'}}>
              Free Diagnostic Test
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DerivativesStrategiesBlog;
